import { postFileRequest, postRequest } from '@/plugins/api-axios'
import { i18n } from '@/plugins/i18n'
import { API, BlockAPI } from '@editorjs/editorjs'
import { debounce } from 'lodash'
import { getFileContentHash } from '@/utils/file'

const Float = { left: 'left', right: 'right' }
const FloatAttr = 'data-float'
const DefaultImageWidth = 500

export default class VBITImage {
    url = process.env.VUE_APP_VBIT_API
    api: API
    blockAPI: BlockAPI
    data: Record<string, any>
    config: any
    readOnly: boolean
    maxWidth = 0
    float: string | null = null
    attachmentId: string | null = null
    fileId: number | null = null
    size: number | null = null
    filename: string | null = null
    caption: string | null = null
    _wrapper!: HTMLDivElement
    _imageContainer!: HTMLDivElement
    _imageLink: HTMLAnchorElement | null = null
    _leftHandle!: HTMLDivElement
    _rightHandle!: HTMLDivElement
    $t: any
    $store: any
    pageX = 0
    isDragging = false

    static get toolbox() {
        return {
            title: i18n.t('form_fe["richTextField_text_toolImageAttachment"]'),
            icon: '<i class="far fa-image"></i>',
        }
    }

    static get isReadOnlySupported() {
        return true
    }

    constructor({ api, block, data, config, readOnly }) {
        this.api = api
        this.blockAPI = block
        this.data = data
        this.config = config || {}
        this.readOnly = readOnly || false
        this.$t = i18n.t.bind(i18n)
        this._initData()
        this._initElements()
    }

    get CSS() {
        return {
            block: this.api.styles.block,
            imageBlock: 'ce-block-image',
            imageBlockResizing: 'ce-block-image--resizing',
            settingsButton: this.api.styles.settingsButton,
            settingsButtonActive: this.api.styles.settingsButtonActive,
            wrapper: 'vbit-image',
            resizeHandle: [
                'tw-relative',
                'tw-place-content-between',
                'tw-cursor-ew-resize',
                'tw-bg-blue-300',
            ],
        }
    }

    renderSettings() {
        const settings = [
            // {
            //     name: this.$t(`form_fe['richTextField.text.floatLeft']`),
            //     value: Float.left,
            //     icon: `<svg xmlns="http://www.w3.org/2000/svg" fill="none" width="20" height="20" viewBox="0 0 15 15"><g fill="#000"><path d="M1.5 0A1.5 1.5 0 0 0 0 1.5v4A1.5 1.5 0 0 0 1.5 7h4A1.5 1.5 0 0 0 7 5.5v-4A1.5 1.5 0 0 0 5.5 0h-4ZM9 2h6V1H9v1ZM9 6h6V5H9v1ZM0 10h15V9H0v1ZM0 14h15v-1H0v1Z"/></g></svg>`,
            // },
            {
                name: this.$t(`form_fe['richTextField.text.floatRight']`),
                value: Float.right,
                icon: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none" viewBox="0 0 15 15"><g fill="#000"><path d="M9.5 0A1.5 1.5 0 0 0 8 1.5v4A1.5 1.5 0 0 0 9.5 7h4A1.5 1.5 0 0 0 15 5.5v-4A1.5 1.5 0 0 0 13.5 0h-4ZM0 2h6V1H0v1ZM0 6h6V5H0v1ZM0 10h15V9H0v1ZM0 14h15v-1H0v1Z"/></g></svg>`,
            },
        ]
        const wrapper = this._make('div', ['tw-mb-4', 'tw-mt-2'])
        const floatOptions = this._make('div', ['tw-flex', 'tw-gap-2'])

        settings.forEach((tune) => {
            const button = this._make(
                'button',
                [
                    'tw-flex',
                    'tw-gap-2',
                    'tw-justify-start',
                    'tw-px-2',
                    'tw-mb-1',
                    this.CSS.settingsButton,
                ],
                { title: tune.name, type: 'button' }
            )
            button.innerHTML = tune.icon
            button.innerHTML += tune.name
            button.classList.toggle(this.CSS.settingsButtonActive, this.float === tune.value)
            button.addEventListener('click', () => {
                this._setFloat(tune.value)
                floatOptions.querySelectorAll(`.${this.CSS.settingsButton}`).forEach((button) => {
                    button.classList.remove(this.CSS.settingsButtonActive)
                })
                button.classList.toggle(this.CSS.settingsButtonActive, this.float === tune.value)
            })
            floatOptions.appendChild(button)
        })
        wrapper.appendChild(floatOptions)

        const captionContainer = this._make('div', ['tw-mt-2', 'tw-flex', 'tw-flex-col'])
        const captionInputId = 'vbitImage-settingInput--caption'
        const captionLabel = this._make('label', ['tw-mb-1', 'tw-inline-block'], {
            for: captionInputId,
        })
        captionLabel.innerText = this.$t(`form_fe['revision.placeholder_imageCaption']`).toString()
        captionContainer.appendChild(captionLabel)

        const captionInput = this._make('input', 'vbitImage-settingInput', {
            id: captionInputId,
            value: this.caption || '',
            ...(this.readOnly && { readOnly: this.readOnly }),
        })
        captionInput.addEventListener('input', (event) => {
            this.caption = (event.target as HTMLInputElement)?.value
            this.blockAPI.dispatchChange()
        })
        captionContainer.appendChild(captionInput)
        wrapper.appendChild(captionContainer)

        return wrapper
    }

    removed() {
        if (this.attachmentId) {
            this.config.onRemoved?.(this.attachmentId)
        }
    }

    render() {
        const container = this._make('div', 'attachment-input')
        container.innerHTML = `<i class="far fa-paperclip"></i><span class="mx-2">
            ${this.$t('fe_hse_module.btn_upload_new_file')}
        </span>`

        const upload = (file) => {
            container.classList.add('tw-hidden')

            // loading spinner
            const loadingEl = this._make('div', 'tw-text-center')
            loadingEl.innerHTML = `<div class="v-progress-circular v-progress-circular--visible v-progress-circular--indeterminate primary--text" style="height: 24px; width: 24px;"><svg xmlns="http://www.w3.org/2000/svg" style="transform:rotate(0deg)" viewBox="21.429 21.429 42.857 42.857"><circle cx="42.857" cy="42.857" r="20" fill="transparent" class="v-progress-circular__overlay"/></svg><div class="v-progress-circular__info"></div></div>`
            this._wrapper.appendChild(loadingEl)

            this._postFile(file).then((fileId) => {
                if (!fileId) {
                    container.classList.remove('tw-hidden')
                    this._wrapper.removeChild(loadingEl)
                }
            })
        }

        const file = this._make('input', null, { type: 'file' })
        file.addEventListener('change', (event) => {
            const files = (event.currentTarget as HTMLInputElement)?.files
            if (!files || !files.length) return
            upload(files[0])
        })

        container.appendChild(file)
        this._wrapper.appendChild(container)

        if (this.fileId) {
            this._renderImage()
        } else if (this.data.file) {
            upload(this.data.file)
        }

        setTimeout(() => {
            this.blockAPI.holder.classList.add(this.CSS.imageBlock)
        }, 0)

        return this._wrapper
    }

    save() {
        return {
            attachmentId: this.attachmentId,
            fileId: this.fileId,
            size: this.size,
            filename: this.filename,
            float: this.float,
            maxWidth: this.maxWidth || null,
            caption: this.caption || '',
        }
    }

    validate(savedData) {
        return savedData.fileId && savedData.size && savedData.filename
    }

    async _setFloat(value) {
        if (value === this.float) {
            this.float = null
            this._imageContainer.removeAttribute(FloatAttr)
        } else {
            this.float = value
            this._imageContainer.setAttribute(FloatAttr, value)
        }
        this._setHandleStyles()
    }

    _setHandleStyles(hidden = false) {
        if (this.readOnly || hidden) {
            this._leftHandle.style.display = 'none'
            this._rightHandle.style.display = 'none'
            return
        }

        this._leftHandle.style.display = this.float === Float.right ? 'grid' : 'none'
        this._rightHandle.style.display = this.float === Float.right ? 'none' : 'grid'
    }

    _initData() {
        this.attachmentId = this.data.attachmentId
        this.fileId = this.data.fileId
        this.size = this.data.size
        this.filename = this.data.filename
        this.caption = this.data.caption
        this.float = this.data.float || null
        this.maxWidth = this.data.maxWidth
    }

    _initElements() {
        this._wrapper = this._make('div', [this.CSS.wrapper, this.CSS.block])
        this._imageContainer = this._make('div', 'tw-inline-flex', {
            [FloatAttr]: this.float,
        })

        const handle = this._make('span', ['tw-text-blue-600', 'tw-rounded-sm'])
        this._leftHandle = this._make('div', this.CSS.resizeHandle, { 'data-handle': 'left' })
        this._leftHandle.appendChild(handle.cloneNode())
        this._leftHandle.appendChild(handle.cloneNode())
        this._leftHandle.appendChild(handle.cloneNode())

        this._rightHandle = this._make('div', this.CSS.resizeHandle, {
            'data-handle': 'right',
        })
        this._rightHandle.appendChild(handle.cloneNode())
        this._rightHandle.appendChild(handle.cloneNode())
        this._rightHandle.appendChild(handle.cloneNode())

        this._setHandleStyles(true)

        this.api.listeners.on(this._leftHandle, 'mousedown', this._onDown)
        this.api.listeners.on(this._rightHandle, 'mousedown', this._onDown)
        this.api.listeners.on(document.body, 'mousemove', this._onMove)
        this.api.listeners.on(document.body, 'mouseup', this._onUp)
    }

    _onUp = (event) => {
        if (!this.isDragging || !(event instanceof MouseEvent)) return

        this.isDragging = false
        let deltaX = event.pageX - this.pageX
        if (this.float === Float.right) deltaX *= -1
        const width = this.maxWidth + deltaX
        this.maxWidth = Math.min(width, this._wrapper.clientWidth || width)
        this._setImage()
        this.blockAPI.dispatchChange()
        this.blockAPI.holder.classList.toggle(this.CSS.imageBlockResizing)
        document.body.style.cursor = ''
    }

    _onDown = (event) => {
        if (!(event instanceof MouseEvent)) return
        this.pageX = event.pageX
        this.isDragging = true
        this.blockAPI.holder.classList.toggle(this.CSS.imageBlockResizing)
        document.body.style.cursor = 'ew-resize'
    }

    _onMove = (event) => {
        if (!this.isDragging) return

        let deltaX = event.pageX - this.pageX
        let wrapperOffset = 0
        if (this.float === Float.right) {
            wrapperOffset = 16 // space between image and floating content (defined in vbit-image.less)
            deltaX *= -1
        }
        const width = this.maxWidth + deltaX
        if (width <= this._wrapper.clientWidth - wrapperOffset) {
            this._imageContainer.style.width = `${width}px`
        }
    }

    destroy() {
        this.api.listeners.off(this._leftHandle, 'mousedown', this._onDown)
        this.api.listeners.off(this._rightHandle, 'mousedown', this._onDown)
        this.api.listeners.off(document.body, 'mousemove', this._onMove)
        this.api.listeners.off(document.body, 'mouseup', this._onUp)
    }

    async _postFile(params) {
        this.fileId = null
        this.size = params.size
        let nameArray = params.name.split('.')
        let fileType = '.' + nameArray.pop().toLowerCase()
        this.filename =
            encodeURIComponent(nameArray.join('.'))
                .replace(/['()]/g, escape)
                .replace(/\*/g, '%2A')
                .replace(/%(?:7C|60|5E)/g, unescape) + fileType
        let url = `https://attachment.${this.url}/files`
        const baseHeaders = {
            'Content-Checksum': `${await getFileContentHash(params)}`,
            'Content-Type': params.type,
        }

        // Trial upload
        let trialHeaders = {
            ...baseHeaders,
            'Content-Disposition': `attachment; filename*=UTF-8''${this.filename}; size=${this.size} `,
        }

        let res = await postFileRequest(url, trialHeaders).catch((error) => {
            error.data.response.data.errors.map((error) => {
                if (error.detail == "The header 'Content-Disposition' contained an invalid value") {
                    this.$store.dispatch('errorSnackbar', 'Wrong file name')
                } else {
                    this.$store.dispatch('errorSnackbar', 'Problems with uploading the file')
                }
            })
        })

        if (res && res.data.status == '200') {
            this.fileId = res.data.data.fileId
        }

        // No existing files upload
        if (res && res.data.status == '204') {
            let headers = {
                ...baseHeaders,
                'Content-Disposition': `attachment; filename*=UTF-8''${this.filename} `,
            }

            let file = await postFileRequest(url, headers, params).catch(() => {
                this.$store.dispatch('errorSnackbar', 'Problems with uploading the file')
            })
            this.fileId = file.data.data.fileId
        }

        this._renderImage()

        if (this.fileId && this.config.links?.related) {
            const { data } = await postRequest(
                this.config.links.related,
                {},
                {
                    type: this.config.type,
                    attributes: {
                        fileId: this.fileId,
                    },
                    relationships: this.config.relationships,
                }
            )
            this.attachmentId = data.data.id
            this.blockAPI.dispatchChange()
        }

        return this.fileId
    }

    _debouncedRenderImage = debounce(this._renderImage, 500)

    _setImage() {
        if (!this._imageLink) {
            this._imageLink = this._make('a', 'tw-flex-grow', {
                target: '_blank',
                draggable: false,
            })
            const image = this._make('img', 'tw-w-full', { draggable: false })
            image.onload = () => {
                if (!this.maxWidth) {
                    this.maxWidth = Math.min(image.width, DefaultImageWidth)
                }
                this._setHandleStyles()
            }
            this._imageLink.appendChild(image)
        }

        const previewUrl = `https://attachment.${this.url}/files/${
            this.fileId
        }/preview?maxImgWidth=${this.maxWidth || DefaultImageWidth}&maxWaitSeconds=20`
        this._imageLink.setAttribute('href', previewUrl)
        this._imageLink.querySelector('img')?.setAttribute('src', previewUrl)

        return this._imageLink
    }

    _renderImage() {
        if (!this.fileId) return

        const image = this._setImage()
        this._setHandleStyles()
        this._imageContainer.appendChild(this._leftHandle)
        this._imageContainer.appendChild(image)
        this._imageContainer.appendChild(this._rightHandle)

        this._wrapper.innerHTML = ''
        this._wrapper.appendChild(this._imageContainer)
        if (this.maxWidth) {
            setTimeout(() => {
                this._imageContainer.style.width = `${Math.min(
                    this.maxWidth,
                    this._wrapper.clientWidth || this.maxWidth
                )}px`
            }, 0)
        }
    }

    /**
     * Creates an HTML element with the specified tag name, class names, and attributes.
     */
    _make<T extends keyof HTMLElementTagNameMap>(
        tagName: T,
        classNames: null | string | string[],
        attributes = {}
    ): HTMLElementTagNameMap[T] {
        const el = document.createElement(tagName)

        if (Array.isArray(classNames)) {
            el.classList.add(...classNames)
        } else if (classNames) {
            el.classList.add(classNames)
        }

        for (const attrName in attributes) {
            el.setAttribute(attrName, attributes[attrName])
        }

        return el
    }
}
